import type { OrganizationType, SubscriptionType } from '@kanbu/schema';
import {
  BillingInterval,
  Currency,
  Role,
  SubscriptionTier,
} from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Card } from '@/components/card/Card';

import { ChangeCurrencyAndPlanDialog } from './components/ChangeCurrencyAndPlanDialog';
import { ChangePlanDialog } from './components/ChangePlanDialog';
import { useSubscriptionManager } from './hooks/useSubscriptionManager';
import { Protected } from '../../components/protected/Protected';
import { trpc } from '../../services/trpc';

/**
 * Props for the SubscriptionManager component
 */
export interface SubscriptionManagerProps {
  organization: OrganizationType;
  activeSubscription: SubscriptionType;
  onUpdate: () => void;
}

export function SubscriptionManager({
  organization,
  activeSubscription,
  onUpdate,
}: SubscriptionManagerProps) {
  const { t } = useLingui();
  const orgCurrency = organization.currency ?? Currency.USD;
  const [displayCurrency, setDisplayCurrency] = useState<Currency>(orgCurrency);
  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);
  const [
    isChangeCurrencyAndPlanDialogOpen,
    setIsChangeCurrencyAndPlanDialogOpen,
  ] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier | null>(
    null,
  );
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(
    BillingInterval.Month,
  );

  const {
    data: tierDetails,
    isLoading: isLoadingTiers,
    isError: isTierDetailsError,
    error: tierDetailsError,
  } = trpc.subscriptions.getTierDetails.useQuery(undefined, {
    staleTime: Infinity,
  });

  const { isLoading } = useSubscriptionManager(organization.id);

  // Update display currency if org currency changes
  useEffect(() => {
    setDisplayCurrency(orgCurrency);
  }, [orgCurrency]);

  useEffect(() => {
    if (activeSubscription) {
      setSelectedInterval(
        activeSubscription.billingInterval || BillingInterval.Month,
      );
    } else {
      setSelectedInterval(BillingInterval.Month);
    }
  }, [activeSubscription]);

  // Effect to handle tier details loading error
  useEffect(() => {
    if (isTierDetailsError && tierDetailsError) {
      console.error('Failed to load tier details:', tierDetailsError);
      toast.error(
        t`Failed to load subscription plan details. Please refresh the page.`,
      );
    }
  }, [isTierDetailsError, tierDetailsError, t]); // Add t to dependency array for lingui

  const subscriptionTiers = tierDetails;

  const handlePlanChange = (plan: SubscriptionTier) => {
    setSelectedPlan(plan);
    const tier = tierDetails?.find(t => t.id === plan);
    if (tier) {
      const hasMonthly = tier.prices.some(
        p => p.interval === BillingInterval.Month && p.currency === orgCurrency,
      );
      const hasYearly = tier.prices.some(
        p => p.interval === BillingInterval.Year && p.currency === orgCurrency,
      );
      if (hasMonthly && !hasYearly) {
        setSelectedInterval(BillingInterval.Month);
      } else if (!hasMonthly && hasYearly) {
        setSelectedInterval(BillingInterval.Year);
      }
    }
    setIsChangePlanDialogOpen(true);
  };

  if (isLoadingTiers || isLoading) {
    return (
      <div className='p-4 text-center'>
        <Trans>Loading subscription details...</Trans>
      </div>
    );
  }

  // Check if user is on free plan
  const isFreePlan =
    !activeSubscription || activeSubscription.tier === SubscriptionTier.Free;

  return (
    <div className='space-y-6 pb-8'>
      <div className='space-y-4'>
        <h3 className='text-xl font-semibold'>
          <Trans>Available Plans</Trans>
        </h3>
        {isLoadingTiers ? (
          <div className='p-4 text-center'>
            <Trans>Loading plans...</Trans>
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3'>
            {/* Filter out Enterprise tier from the standard display */}
            {subscriptionTiers
              ?.filter(tier => tier.id !== SubscriptionTier.Enterprise)
              .map(tier => {
                const currentActiveTier =
                  activeSubscription?.tier || SubscriptionTier.Free;

                if (
                  tier.id === SubscriptionTier.Free &&
                  currentActiveTier !== SubscriptionTier.Free
                ) {
                  return null;
                }

                const isActive = tier.id === currentActiveTier;

                // Find prices in the display currency (for free tier) or org currency (for paid plans)
                const currencyToShow = isFreePlan
                  ? displayCurrency
                  : orgCurrency;

                const monthlyPriceInfo = tier.prices.find(
                  p =>
                    p.interval === BillingInterval.Month &&
                    p.currency === currencyToShow,
                );
                const yearlyPriceInfo = tier.prices.find(
                  p =>
                    p.interval === BillingInterval.Year &&
                    p.currency === currencyToShow,
                );

                return (
                  <Card
                    key={tier.id}
                    title={tier.name}
                    collapsible={false}
                    className={`flex h-full flex-col ${isActive ? 'border-primary ring-2 ring-primary' : ''}`}
                  >
                    <div className='min-h-[230px] grow'>
                      <div className='mb-4 flex items-baseline gap-1'>
                        <span className='text-3xl font-bold'>
                          {tier.id === SubscriptionTier.Free
                            ? t`Free`
                            : monthlyPriceInfo
                              ? formatUtils.currency(
                                  monthlyPriceInfo.amount,
                                  currencyToShow,
                                )
                              : t`N/A`}
                        </span>
                        {tier.id !== SubscriptionTier.Free &&
                          monthlyPriceInfo && (
                            <span className='text-sm text-gray-500'>
                              / {t`mo`}
                            </span>
                          )}
                      </div>
                      {tier.id !== SubscriptionTier.Free && yearlyPriceInfo && (
                        <div className='-mt-3 mb-4 text-sm text-gray-500'>
                          {monthlyPriceInfo ? t`or ` : ''}
                          {formatUtils.currency(
                            yearlyPriceInfo.amount,
                            currencyToShow,
                          )}{' '}
                          / {t`yr`}
                        </div>
                      )}

                      <div className='mb-6 space-y-2'>
                        {tier.id !== SubscriptionTier.Free &&
                          tier.colleaguesSubstitute != null &&
                          tier.colleaguesSubstitute !== 0 && (
                            <p
                              key='colleagues-substitute'
                              className='flex items-center text-sm'
                            >
                              <Check className='mr-2 size-4 shrink-0 text-primary' />
                              <span>
                                {typeof tier.colleaguesSubstitute ===
                                'number' ? (
                                  <>
                                    <Trans>
                                      Colleagues substituted: up to{' '}
                                      {tier.colleaguesSubstitute}
                                    </Trans>{' '}
                                    <span className='text-gray-500'>
                                      (
                                      {tier.colleaguesSubstitute < 1
                                        ? t`part-time`
                                        : t`full-time`}
                                      )
                                    </span>
                                  </>
                                ) : (
                                  <Trans>
                                    Colleagues substituted:{' '}
                                    {tier.colleaguesSubstitute}
                                  </Trans>
                                )}
                              </span>
                            </p>
                          )}
                        {tier.id !== SubscriptionTier.Free &&
                          typeof tier.maxConversations === 'number' &&
                          tier.maxConversations > 0 && (
                            <>
                              <p
                                key='max-conversations'
                                className='flex items-center text-sm'
                              >
                                <Check className='mr-2 size-4 shrink-0 text-primary' />
                                <span>
                                  <Trans>
                                    Resolved conversations:{' '}
                                    {tier.maxConversations}
                                  </Trans>
                                </span>
                              </p>
                              <p
                                key='ai-messages'
                                className='flex items-center text-sm'
                              >
                                <Check className='mr-2 size-4 shrink-0 text-primary' />
                                <span>
                                  <Trans>
                                    AI sent messages:{' '}
                                    {tier.maxConversations * 3}
                                  </Trans>
                                </span>
                              </p>
                            </>
                          )}
                        {tier.id !== SubscriptionTier.Free &&
                          typeof tier.maxDocuments === 'number' &&
                          tier.maxDocuments > 0 && (
                            <p
                              key='max-docs'
                              className='flex items-center text-sm'
                            >
                              <Check className='mr-2 size-4 shrink-0 text-primary' />
                              <span>
                                <Trans>
                                  Data sources: {tier.maxDocuments} documents
                                </Trans>
                              </span>
                            </p>
                          )}
                        {tier.id !== SubscriptionTier.Free &&
                          tier.maxPages != null &&
                          tier.maxPages !== 0 && (
                            <p
                              key='max-pages'
                              className='flex items-center text-sm'
                            >
                              <Check className='mr-2 size-4 shrink-0 text-primary' />
                              <span>
                                <Trans>Pages: {tier.maxPages}</Trans>
                              </span>
                            </p>
                          )}
                        {tier.features?.map(feature => (
                          <p
                            key={feature}
                            className='flex items-center text-sm'
                          >
                            <Check className='mr-2 size-4 shrink-0 text-primary' />
                            <span>{feature}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className='mt-auto'>
                      {isActive ? (
                        <p className='text-center text-lg text-primary'>
                          <Trans>You are currently on this plan.</Trans>
                        </p>
                      ) : (
                        <Button
                          data-cta='admin-subscription-manager-change-plan-button'
                          className='mt-auto w-full'
                          variant={isActive ? 'secondary' : 'primary'}
                          onClick={() => {
                            if (!isActive) {
                              handlePlanChange(tier.id);
                            }
                          }}
                          disabled={isActive || isLoadingTiers}
                          icon={
                            isActive ? undefined : (
                              <ArrowRight className='ml-2 size-4' />
                            )
                          }
                        >
                          <Trans>Change Plan</Trans>
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            <Card
              key='enterprise-static'
              title={<Trans>Enterprise</Trans>}
              collapsible={false}
              className='relative flex h-full flex-col'
            >
              <div className='justify-between'>
                <div>
                  <p className='mb-4 text-sm'>
                    <Trans>
                      Need a custom solution with specific limits, features, or
                      dedicated support?
                    </Trans>
                  </p>
                  <ul className='mb-6 list-inside list-disc space-y-1 text-sm'>
                    <li>
                      <Trans>Custom usage allowances</Trans>
                    </li>
                    <li>
                      <Trans>Custom user/document limits</Trans>
                    </li>
                    <li>
                      <Trans>Dedicated support options</Trans>
                    </li>
                    <li>
                      <Trans>Volume discounts</Trans>
                    </li>
                  </ul>
                </div>
                <Button
                  data-cta='admin-subscription-manager-contact-us-button'
                  className='mt-auto w-full'
                  variant='secondary'
                  onClick={() => {
                    window.open(
                      'https://app.reclaim.ai/m/kanbu/demo',
                      '_blank',
                    );
                  }}
                >
                  <Trans>Contact Us for Details</Trans>
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {selectedPlan && (
        <ChangePlanDialog
          isOpen={isChangePlanDialogOpen}
          onClose={() => setIsChangePlanDialogOpen(false)}
          currentTier={activeSubscription?.tier || SubscriptionTier.Free}
          selectedTier={selectedPlan}
          organizationId={organization.id}
          onSubscriptionUpdate={() => {
            setIsChangePlanDialogOpen(false);
            if (onUpdate) onUpdate();
          }}
          selectedInterval={selectedInterval}
        />
      )}

      {/* Super Admin Change Currency and Plan Dialog */}
      <Protected roles={[Role.SuperAdmin]}>
        {activeSubscription && (
          <ChangeCurrencyAndPlanDialog
            isOpen={isChangeCurrencyAndPlanDialogOpen}
            onClose={() => setIsChangeCurrencyAndPlanDialogOpen(false)}
            organizationId={organization.id}
            currentCurrency={orgCurrency}
            currentTier={activeSubscription.tier ?? undefined}
            currentInterval={activeSubscription.billingInterval ?? undefined}
            onSuccess={() => {
              setIsChangeCurrencyAndPlanDialogOpen(false);
              if (onUpdate) onUpdate();
            }}
          />
        )}
      </Protected>
    </div>
  );
}
